/**
 * LeavesDataTable
 *
 * The Congés list, in the layout of the arrêts suivi (WorkStoppagesDataTable): an MUI DataGrid
 * in the same card, a search box, predefined status filters as counted chips, status chips
 * in one palette, and the row actions pinned on the right — details, "see in the planning",
 * and validate / reject / delete for whoever manages the leave.
 *
 * Presentational: the rows, what each action does and who may do it come from LeavesView.
 */
import React, { useCallback, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { Box, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import { useSelector } from "react-redux";
import moment from "moment";
import { Localize } from "../../app/lang/langs";
import { getDayPeriodFromDate } from "../../app/utils/helpers";
import styles from "../collabs/CollabsView.module.css";
import { usePinnedActionsColumn, singleSelectFilterOperator } from "../collabs/collabsTableUtils";
import { StatusDot } from "../../components/workStoppages/WorkStoppageStatus";

/** A leave's status colours — the arrêts palette, extended to the leave statuses. */
export const LEAVE_STATUS_PALETTE = {
  // A leave placed in the planning but not yet submitted for validation.
  CREATED: { fg: "#0369A1", bg: "#E0F2FE" },
  PENDING: { fg: "#B45309", bg: "#FFF4E5" },
  VALIDATED: { fg: "#15803D", bg: "#E7F6EC" },
  REJECTED: { fg: "#B91C1C", bg: "#FDECEC" },
  DELETE_REQUESTED: { fg: "#8E24AA", bg: "#F3E5F5" },
  DELETED: { fg: "#5F6B7A", bg: "#EEF0F3" },
};
export const LEAVE_STATUSES = Object.keys(LEAVE_STATUS_PALETTE);
const NEUTRAL = { fg: "#5F6B7A", bg: "#EEF0F3" };

/** A leave status as a coloured chip, like the arrêts' StatusChip. */
export function LeaveStatusChip({ status, sx }) {
  if (!status) return null;
  const palette = LEAVE_STATUS_PALETTE[status] || NEUTRAL;
  return (
    <Chip
      size="small"
      label={Localize(`LEAVE_STATUS_${status}`)}
      sx={{
        fontWeight: 600,
        borderRadius: "8px",
        color: palette.fg,
        bgcolor: palette.bg,
        border: `1px solid ${palette.fg}33`,
        ...sx,
      }}
    />
  );
}

/** The predefined filters, applied before the grid's own so the two combine. */
const LEAVE_PRESETS = [
  { value: "ALL", color: NEUTRAL.fg, test: () => true },
  { value: "PENDING", color: LEAVE_STATUS_PALETTE.PENDING.fg, test: (row) => row.status === "PENDING" },
  { value: "DELETE_REQUESTED", color: LEAVE_STATUS_PALETTE.DELETE_REQUESTED.fg, test: (row) => row.status === "DELETE_REQUESTED" },
  { value: "VALIDATED", color: LEAVE_STATUS_PALETTE.VALIDATED.fg, test: (row) => row.status === "VALIDATED" },
  { value: "REJECTED", color: LEAVE_STATUS_PALETTE.REJECTED.fg, test: (row) => row.status === "REJECTED" },
  {
    value: "ONGOING", color: "#3949AB",
    test: (row) => row.status === "VALIDATED" && !!row.toDate && !moment(row.toDate).isBefore(moment(), "day"),
  },
];

/** Empty state — before anything is loaded and when a filter matches nothing. */
function NoRowsOverlay() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(0,0,0,0.5)" }}>
      {Localize("LEAVES_EMPTY")}
    </div>
  );
}

/** A leave bound as the Congés list writes it: the day and its half day. */
const renderBoundCell = (params) => (params.value
  ? `${moment(params.value).format("DD/MM/YYYY")} ${getDayPeriodFromDate(params.value) || ""}`.trim()
  : "");

const renderDateTimeCell = (params) => (params.value ? moment(params.value).format("DD/MM/YYYY HH:mm") : "");

/**
 * @param {Object} props
 * @param {Object} props.apiRef - MUI useGridApiRef() ref
 * @param {Array} props.data - the leaves (`state.leaves.list`)
 * @param {Array} props.sampleLeaves - leave types, to name and colour the type column
 * @param {string} props.scrollHeight
 * @param {Function} props.canManage - (row) => whether the viewer manages that leave
 * @param {string|number} props.userId
 * @param {Function} props.onViewDetails - (row) => opens the leave
 * @param {Function} props.onOpenPlanning - (row) => opens the planning on the leave's first day
 * @param {Function} props.onValidate - (row)
 * @param {Function} props.onReject - (row)
 * @param {Function} props.onDelete - (row) — a manager's delete
 * @param {Function} props.onRequestDelete - (row) — a collab's request to delete their own
 */
export default function LeavesDataTable({
  apiRef,
  data,
  sampleLeaves,
  scrollHeight,
  canManage,
  userId,
  onViewDetails,
  onOpenPlanning,
  onValidate,
  onReject,
  onDelete,
  onRequestDelete,
}) {
  const lang = useSelector((state) => state.settings.lang);
  const dataGridLocale = lang === "fr" ? frFR : enUS;
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [globalSearch, setGlobalSearch] = useState("");
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [preset, setPreset] = useState("ALL");

  usePinnedActionsColumn(apiRef);

  const presetCounts = useMemo(() => {
    const counts = {};
    LEAVE_PRESETS.forEach((p) => { counts[p.value] = (data || []).filter(p.test).length; });
    return counts;
  }, [data]);

  const rows = useMemo(() => {
    const definition = LEAVE_PRESETS.find((p) => p.value === preset);
    return definition ? (data || []).filter(definition.test) : data || [];
  }, [data, preset]);

  // One leave type per code: sample_leaves has a row per country, same code and colour.
  const typesByCode = useMemo(() => {
    const map = {};
    (sampleLeaves || []).forEach((sample) => { if (!map[sample.type]) map[sample.type] = sample; });
    return map;
  }, [sampleLeaves]);

  /**
   * The actions a row shows, so the pinned column is only as wide as its busiest row needs —
   * a fixed width sized for a manager's five buttons left a gap beside the usual three.
   */
  const actionCount = useCallback((row) => {
    const manages = canManage(row);
    const isOwn = String(row.id_collab) === String(userId);
    let count = 1; // see in the planning
    if (manages) count += 1; // details
    if (manages && row.status === "PENDING") count += 2; // validate, reject
    if (manages ? row.status !== "DELETED" : isOwn && !["DELETED", "DELETE_REQUESTED"].includes(row.status)) count += 1;
    return count;
  }, [canManage, userId]);

  // 30px per small icon button, plus the cell's own padding.
  const actionsWidth = useMemo(
    () => Math.max(1, ...(rows || []).map(actionCount)) * 30 + 24,
    [rows, actionCount]
  );

  const columns = useMemo(() => [
    { field: "id_silae", headerName: Localize("Matricule", lang), minWidth: 120, sortable: true, filterable: true },
    { field: "company", headerName: Localize("Company", lang), minWidth: 140, sortable: true, filterable: true },
    { field: "prenom", headerName: Localize("Name", lang), minWidth: 140, sortable: true, filterable: true },
    { field: "nom", headerName: Localize("Family Name", lang), minWidth: 140, sortable: true, filterable: true },
    // Today's team, next to the team the leave was taken in: who to talk to about it now.
    { field: "currentTeamName", headerName: Localize("WORK_STOPPAGE_CURRENT_TEAM", lang), minWidth: 160, sortable: true, filterable: true },
    { field: "teamName", headerName: Localize("LEAVE_TEAM", lang), minWidth: 150, sortable: true, filterable: true },
    {
      field: "type", headerName: Localize("Type", lang), minWidth: 190, sortable: true, filterable: true,
      valueGetter: (value) => {
        const sample = typesByCode[value];
        return sample ? sample.label : value;
      },
      renderCell: (params) => {
        const sample = typesByCode[params.row.type];
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <StatusDot color={sample?.color || NEUTRAL.fg} />
            <span style={{ whiteSpace: "normal" }}>{params.value}</span>
          </Stack>
        );
      },
    },
    {
      field: "status", headerName: Localize("Status", lang), minWidth: 170, sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator(LEAVE_STATUSES.map((status) => ({
        value: status, label: Localize(`LEAVE_STATUS_${status}`, lang),
      })))],
      renderCell: (params) => <LeaveStatusChip status={params.value} />,
    },
    {
      field: "fromDate", headerName: Localize("Start", lang), minWidth: 150,
      type: "dateTime", sortable: true, filterable: true,
      valueGetter: (value) => (value ? new Date(value) : null),
      renderCell: renderBoundCell,
    },
    {
      field: "toDate", headerName: Localize("End", lang), minWidth: 150,
      type: "dateTime", sortable: true, filterable: true,
      valueGetter: (value) => (value ? new Date(value) : null),
      renderCell: renderBoundCell,
    },
    {
      field: "duration", headerName: Localize("Duration", lang), minWidth: 110, type: "number", sortable: true, filterable: true,
      // Stored as "2.5 j": the number sorts, the label is rebuilt for display.
      valueGetter: (value) => (value ? parseFloat(value) : null),
      renderCell: (params) => (params.value === null || Number.isNaN(params.value) ? "" : `${params.value} j`),
    },
    {
      field: "hasAttachment", headerName: Localize("ATTACHMENT", lang), minWidth: 110, type: "boolean",
      sortable: true, filterable: true, align: "center", headerAlign: "center",
      // Only for rows the viewer manages — whether a colleague's leave has a document
      // attached is not everyone's business.
      renderCell: (params) => (canManage(params.row) && params.value ? (
        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: "#2f7d32" }}>
          <AttachFileRoundedIcon fontSize="small" />
          {params.row.attachmentsCount > 1 ? <span style={{ fontSize: 12 }}>{params.row.attachmentsCount}</span> : null}
        </Stack>
      ) : null),
    },
    {
      field: "createdAtTime", headerName: Localize("CREATED_AT", lang), minWidth: 150,
      type: "dateTime", sortable: true, filterable: true,
      valueGetter: (value) => (value ? new Date(value) : null),
      renderCell: renderDateTimeCell,
    },
    { field: "validatedBy", headerName: Localize("Validated by", lang), minWidth: 150, sortable: true, filterable: true },
    {
      field: "validatedAt", headerName: Localize("Validated at", lang), minWidth: 150,
      type: "dateTime", sortable: true, filterable: true,
      valueGetter: (value) => (value ? new Date(value) : null),
      renderCell: renderDateTimeCell,
    },
    {
      field: "actions", headerName: "", width: actionsWidth, sortable: false, filterable: false,
      disableColumnMenu: true, disableReorder: true,
      renderCell: (params) => {
        const row = params.row;
        const manages = canManage(row);
        const isOwn = String(row.id_collab) === String(userId);
        return (
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ height: "100%" }}>
            {manages && (
              <Tooltip title={Localize("VIEW_DETAILS", lang)}>
                <IconButton size="small" onClick={() => onViewDetails(row)}>
                  <VisibilityRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={Localize("WORK_STOPPAGE_OPEN_PLANNING", lang)}>
              <span>
                <IconButton size="small" disabled={!row.fromDate} onClick={() => onOpenPlanning(row)}>
                  <EventNoteRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {manages && row.status === "PENDING" && (
              <>
                <Tooltip title={Localize("VALIDATE", lang)}>
                  <IconButton size="small" color="success" onClick={() => onValidate(row)}>
                    <CheckCircleOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={Localize("REJECT", lang)}>
                  <IconButton size="small" color="error" onClick={() => onReject(row)}>
                    <CancelOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {manages && row.status !== "DELETED" && (
              <Tooltip title={Localize("Delete", lang)}>
                <IconButton size="small" color="error" onClick={() => onDelete(row)}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!manages && isOwn && !["DELETED", "DELETE_REQUESTED"].includes(row.status) && (
              <Tooltip title={Localize("REQUEST_DELETE", lang)}>
                <IconButton size="small" color="error" onClick={() => onRequestDelete(row)}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ], [lang, typesByCode, canManage, userId, actionsWidth, onViewDetails, onOpenPlanning, onValidate, onReject, onDelete, onRequestDelete]);

  return (
    <div className={styles.collabsCard} style={{ marginTop: 0, padding: "0.75rem 1rem" }}>
      <div className="p-2 mb-1 d-flex align-items-center" style={{ gap: 16 }}>
        <input
          type="text"
          className="form-control"
          placeholder={Localize("Search") + "..."}
          value={globalSearch}
          onChange={(e) => {
            const val = e.target.value;
            setGlobalSearch(val);
            setFilterModel((prev) => ({ ...prev, quickFilterValues: val ? val.split(" ") : [] }));
          }}
          style={{ maxWidth: 350 }}
        />
      </div>
      {/* The same counted chips as the arrêts' WorkStoppagePresetFilters. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 1, pb: 1 }}>
        {LEAVE_PRESETS.map((p) => {
          const selected = preset === p.value;
          return (
            <Chip
              key={p.value}
              size="small"
              clickable
              onClick={() => { setPreset(p.value); setPaginationModel((prev) => ({ ...prev, page: 0 })); }}
              icon={<StatusDot color={selected ? "#fff" : p.color} />}
              label={`${Localize(`LEAVE_PRESET_${p.value}`, lang)} (${presetCounts[p.value]})`}
              aria-pressed={selected}
              sx={{
                fontWeight: 600,
                borderRadius: "8px",
                pl: 0.5,
                "& .MuiChip-icon": { ml: 0.5 },
                color: selected ? "#fff" : "text.primary",
                bgcolor: selected ? p.color : "transparent",
                border: `1px solid ${selected ? p.color : "rgba(0,0,0,0.12)"}`,
                "&:hover": { bgcolor: selected ? p.color : "rgba(0,0,0,0.04)" },
              }}
            />
          );
        })}
      </Box>
      <div style={{ height: scrollHeight, width: "100%" }}>
        <DataGrid
          localeText={dataGridLocale.components.MuiDataGrid.defaultProps.localeText}
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          filterModel={filterModel}
          onFilterModelChange={(newModel) => {
            setFilterModel((prev) => ({ ...newModel, quickFilterValues: prev.quickFilterValues }));
          }}
          // Opens the leave, as the actions' eye does — only where the viewer manages it.
          onRowDoubleClick={(params) => { if (canManage(params.row)) onViewDetails(params.row); }}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          slots={{ noRowsOverlay: NoRowsOverlay }}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center", py: 1 },
            "& .MuiDataGrid-footerContainer": { borderTop: "1px solid #e0e0e0", minHeight: 52 },
            "& .MuiTablePagination-root": { width: "100%", overflow: "hidden" },
            "& .MuiTablePagination-toolbar": { justifyContent: "flex-end", alignItems: "center", minHeight: 52, px: 2 },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { margin: 0 },
            '& .MuiDataGrid-cell[data-field="actions"]': { backgroundColor: "var(--surface-card, #fff)" },
          }}
        />
      </div>
    </div>
  );
}
